/**
 * Healthcare & long-term care (spec §11) — for anyone retiring before 65 this
 * is the largest controllable expense and the most common reason early plans
 * fail.
 *
 * Pre-65 (per person; spouses hit 65 in different years):
 *   - ACA mode: net premium = benchmark − PTC, where PTC = benchmark −
 *     applicable%(MAGI/FPL) × MAGI, floored at 0 — with the 400%-FPL cliff
 *     when in force. MAGI is estimated from the PRIOR year's income (the
 *     within-year circularity is real life too: subsidies reconcile at
 *     filing).
 *   - Override mode: a flat $X/month.
 * 65+: Part B (base + IRMAA from MAGI two years back), Part D surcharge,
 *      Medigap/Advantage, dental/vision, out-of-pocket.
 * LTC: optional per-person events with insurance offsets.
 */
import type { HealthcareConfig, LongTermCareAssumption, Person, Plan } from '../model/types';
import { applicablePercentage, fplFor } from './data/aca';
import { irmaaFor, PART_D_BASE_2026, type IrmaaResult } from './data/medicare';

export const DEFAULT_HEALTHCARE: HealthcareConfig = {
  preMedicareMode: 'aca',
  // KFF 2026 benchmark ($625/mo at 40) scaled by the 3:1 age curve → ~$1,350
  // for the 60-64 band. Varies ~3x by state; user-editable.
  benchmarkMonthlyPremium: 1_350,
  overrideMonthlyPremium: 800,
  oopAnnualPerPerson: 2_500,
  medigapMonthly: 180,
  dentalVisionAnnual: 1_200,
};

export interface PersonHealthcareYear {
  personId: string;
  premium: number;
  acaSubsidy: number;
  oop: number;
}

export interface HealthcareYearResult {
  total: number;
  premiums: number;
  oop: number;
  acaSubsidyTotal: number;
  /** MAGI/FPL ratio used for ACA math (null when nobody is on the ACA). */
  fplRatio: number | null;
  irmaa: IrmaaResult | null; // highest tier applied this year (household)
  perPerson: PersonHealthcareYear[];
}

/**
 * Healthcare costs for the year. `healthcareInflationFactor` compounds the
 * profile's healthcare inflation from the start year.
 *
 * @param magiPriorYear   household MAGI last year (ACA estimate)
 * @param magiTwoYearsAgo household MAGI two years back (IRMAA lookback)
 * @param generalInflationFactor cumulative general inflation since the start
 *        year; scales the IRMAA MAGI floors and the FPL, both CPI-indexed in
 *        law, so nominal income growth alone doesn't trip the cliffs.
 */
export function computeHealthcare(
  plan: Plan,
  people: Person[],
  agesById: Record<string, number>,
  aliveById: Record<string, boolean>,
  anyoneWorking: boolean,
  magiPriorYear: number,
  magiTwoYearsAgo: number,
  healthcareInflationFactor: number,
  generalInflationFactor = 1,
): HealthcareYearResult {
  const hc = plan.healthcare ?? DEFAULT_HEALTHCARE;
  const perPerson: PersonHealthcareYear[] = [];
  let premiums = 0;
  let oop = 0;
  let acaSubsidyTotal = 0;
  let fplRatio: number | null = null;
  let irmaa: IrmaaResult | null = null;

  const householdSize = people.filter((p) => aliveById[p.id]).length +
    plan.household.dependents.length;

  for (const p of people) {
    if (!aliveById[p.id]) continue;
    const age = agesById[p.id]!;
    let premium = 0;
    let subsidy = 0;

    if (age >= 65) {
      // Medicare. IRMAA cliff from the two-years-ago MAGI.
      const tier = irmaaFor(magiTwoYearsAgo, plan.household.filingStatus, generalInflationFactor);
      irmaa = !irmaa || tier.tierIndex > irmaa.tierIndex ? tier : irmaa;
      premium =
        (tier.partBMonthly + PART_D_BASE_2026 + tier.partDSurchargeMonthly + hc.medigapMonthly) *
          12 * healthcareInflationFactor +
        hc.dentalVisionAnnual * healthcareInflationFactor;
    } else if (age >= p.retirementAge && !anyoneWorkingCovers(p, anyoneWorking)) {
      // Retired, pre-Medicare: the danger zone.
      if (hc.preMedicareMode === 'override') {
        premium = hc.overrideMonthlyPremium * 12 * healthcareInflationFactor;
      } else {
        const benchmark = hc.benchmarkMonthlyPremium * 12 * healthcareInflationFactor;
        const fpl = fplFor(Math.max(1, householdSize), generalInflationFactor);
        const ratio = magiPriorYear / fpl;
        fplRatio = ratio;
        const pct = applicablePercentage(ratio);
        if (pct == null) {
          premium = benchmark; // no credit: below 100% FPL or over the cliff
        } else {
          const expectedContribution = pct * magiPriorYear;
          subsidy = Math.max(0, benchmark - expectedContribution);
          premium = benchmark - subsidy;
        }
      }
    }
    // Still working: employer coverage assumed; only OOP applies.

    const personOop = hc.oopAnnualPerPerson * healthcareInflationFactor;
    premiums += premium;
    oop += personOop;
    acaSubsidyTotal += subsidy;
    perPerson.push({ personId: p.id, premium, acaSubsidy: subsidy, oop: personOop });
  }

  return {
    total: premiums + oop,
    premiums,
    oop,
    acaSubsidyTotal,
    fplRatio,
    irmaa,
    perPerson,
  };
}

/** Employer coverage assumption: only the person still working is covered free;
 *  a retired spouse of a worker is treated as covered too (common case). */
function anyoneWorkingCovers(_p: Person, anyoneWorking: boolean): boolean {
  return anyoneWorking;
}

export interface LtcYearResult {
  cost: number;
  insuranceOffset: number;
  insurancePremiums: number;
  net: number;
}

/**
 * Long-term care costs for the year across all enabled events.
 * @param yearsFromStart for inflation compounding
 */
export function computeLtc(
  events: LongTermCareAssumption[] | undefined,
  agesById: Record<string, number>,
  aliveById: Record<string, boolean>,
  yearsFromStart: number,
  defaultLtcInflation: number,
): LtcYearResult {
  let cost = 0;
  let insuranceOffset = 0;
  let insurancePremiums = 0;

  for (const ev of events ?? []) {
    if (!ev.enabled) continue;
    const age = agesById[ev.personId];
    if (age == null || !aliveById[ev.personId]) continue;

    const infl = Math.pow(1 + (ev.inflationRateOverride ?? defaultLtcInflation), yearsFromStart);
    const inCare = age >= ev.onsetAge && age < ev.onsetAge + ev.durationYears;

    if (inCare) {
      const yearCost = ev.annualCostToday * infl;
      cost += yearCost;
      if (ev.insurance) {
        const ins = ev.insurance;
        const yearsIntoCare = age - ev.onsetAge;
        // Benefit pays after the elimination period, for the benefit period,
        // with its inflation rider compounding from the start year.
        const eliminationYears = ins.eliminationPeriodDays / 365;
        const paying =
          yearsIntoCare + 1 > eliminationYears &&
          yearsIntoCare < ins.benefitPeriodYears + eliminationYears;
        if (paying) {
          const riderFactor = Math.pow(1 + ins.inflationRider, yearsFromStart);
          const annualBenefit = ins.dailyBenefit * 365 * riderFactor;
          insuranceOffset += Math.min(annualBenefit, yearCost);
        }
      }
    }

    // Premiums are paid until the end age or until care begins.
    if (ev.insurance && !inCare) {
      const endAge = ev.insurance.premiumEndAge ?? ev.onsetAge;
      if (age < endAge) insurancePremiums += ev.insurance.annualPremium;
    }
  }

  return {
    cost,
    insuranceOffset,
    insurancePremiums,
    net: cost - insuranceOffset + insurancePremiums,
  };
}
